import React, {useMemo} from 'react';
import {P11MatchItem} from '@/pages/home/home.type';
import Pick11Countdown from './pick11-countdown';
import {getImageUrl} from '@/components/utils';

function formatDateTime(timestamp?: number) {
  if (!timestamp) {
    return {time: ''};
  }

  const date = new Date(+timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;

  return {
    time: `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`,
  };
}

const TeamRow = ({
  logo,
  shortName,
  name,
}: {
  logo?: string;
  shortName?: string;
  name?: string;
}) => {
  return (
    <div className="flex min-w-0 items-center gap-2 overflow-hidden">
      <div className="h-8 w-8 flex-shrink-0">
        {logo ? (
          <img
            src={getImageUrl(logo)}
            alt={shortName || name || 'team logo'}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : null}
      </div>
      <span className="flex-shrink-0 text-sm font-bold text-t1">
        {shortName}
      </span>
      <span className="truncate text-sm font-medium text-t3">{name}</span>
    </div>
  );
};

const Pick11Card = ({
  match,
  onClick,
  className,
}: {
  match: P11MatchItem;
  onClick: () => void;
  className?: string;
}) => {
  const {time} = useMemo(
    () => formatDateTime(match.dateStart),
    [match.dateStart],
  );
  const prizePoolDisplay = useMemo(
    () => match.maxPrizePool?.toLocaleString('en-IN') ?? '0',
    [match.maxPrizePool],
  );
  const extraClassName = className || '';

  return (
    <div
      className={`w-full flex cursor-pointer flex-col gap-2 rounded-lg border border-[#E6E8EA] bg-white p-3 shadow-[0_8px_16px_0_rgba(0,0,0,0.04)] ${extraClassName}`}
      onClick={onClick}>
      <div className="anna-pick11-card__header flex items-center justify-between gap-2">
        <div className="anna-pick11-card__title flex items-center gap-1 text-sm text-[#18181B] min-w-0">
          <div className="anna-pick11-card__title-text flex flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {match.fantasyCompetitionTitle}
          </div>
          <img
            src="/images/home/arrow_right.webp"
            alt="arrow"
            className="anna-pick11-card__arrow h-3 w-3 flex-shrink-0"
          />
        </div>
        <img
          src="/images/home/pick_logo.webp"
          alt="Pick11"
          className="anna-pick11-card__badge h-4 w-4 flex-shrink-0"
        />
      </div>

      <div className="anna-pick11-card__content flex items-center gap-3 rounded-lg bg-[#F8F9FB] p-2">
        <div className="anna-pick11-card__teams flex min-w-0 flex-1 flex-col gap-[6px]">
          <TeamRow
            logo={match.teamaLogoUrl}
            shortName={match.teamaShortName}
            name={match.teamaName}
          />
          <TeamRow
            logo={match.teambLogoUrl}
            shortName={match.teambShortName}
            name={match.teambName}
          />
        </div>
        <div className="anna-pick11-card__time relative pl-6 before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-[#E6E8EA] before:content-['']">
          <div className="anna-pick11-card__time-inner flex h-full flex-col items-center justify-center gap-1">
            {match.status === 'upcoming' && (
              <Pick11Countdown
                startTime={match.dateStart}
                endTime={match.dateEnd}
              />
            )}
            <span className="text-xs font-medium tracking-[-0.01875rem] text-black">
              {time}
            </span>
          </div>
        </div>
      </div>

      <div className="anna-pick11-card__footer w-full text-xs font-bold text-[#727272]">
        EST.MEGA ₹{prizePoolDisplay}
      </div>
    </div>
  );
};

export default Pick11Card;
